/**
 * 서비스 안내 챗봇 API 클라이언트 (WBS 3).
 *
 * 계약 원본: `docs/2026-08-20-chat-front-contract.md` (프론트 제안 + 확정분)
 * 상위 정본: `wbs3-contract-decisions.md` → `lawkit-api/docs/2026-08-18-chatbot-design.md`
 *
 * **이 파일이 계약을 아는 유일한 자리다.** 백엔드가 뜨거나 형식이 바뀌면 여기만 고친다 —
 * 화면 컴포넌트에는 엔드포인트도, 봉투도, 목 전환도 새지 않는다.
 *
 * 지켜야 할 셋 (설계 §1 · goal §3):
 *   ① `cta` 를 프론트가 다시 계산하지 않는다. `priceStatus`·`instantPayable` 을 조합하는
 *      코드가 이 레포에 없어야 한다 — 분기 규칙이 두 군데면 반드시 갈린다.
 *   ② 금액을 계산하지 않는다. `totalAmount`·`supplyAmount` 를 그대로 찍는다.
 *   ③ **문장을 코드에 박지 않는다.** copy key 상수가 0개다 — 서버가 완성된 문장을 싣는다
 *      (버튼 라벨까지). 하드코딩하면 어드민 편집이 성립하지 않는다.
 */

import type { AxiosResponse } from 'axios';
import { axiosInstance } from '@/lib/axios';
import { apiErrorMessage } from '@/utils/apiError';
import type { CatalogProduct } from './catalog';
import { ChatApiError } from './chat.error';
import * as mock from './chat.mock';

/**
 * 목 응답으로 도는가.
 *
 * 백엔드와 병렬로 화면을 먼저 완성하려고 둔 스위치였다(goal §6). **2026-09-02 에 껐다** —
 * 백엔드(lawkit-api PR #67, 되묻기 5회 + 대화 맥락)가 dev 에 배포되고 DDL 까지 적용된
 * 뒤다. 이제 이 화면은 실제 파이프라인(Gemini 분류 → 서버 템플릿 조립 → 가드레일 →
 * IP 제한)으로 돈다.
 *
 * 목 파일(`chat.mock.ts` · `chat.mock.data.ts`)은 당분간 남긴다 — 계약 회귀 테스트가 목을
 * 직접 물고 있고, 로컬에서 백엔드 없이 화면을 볼 때 이 값만 잠깐 켜면 된다. 백엔드가
 * 안정되면 함께 지운다.
 *
 * `.env*` 로 빼지 않았다 — `.env`·`.env.dev`·`.env.prod` 셋 다 git 추적 중이고 이 레포에
 * 워크트리가 8개 돌고 있어서, 공유 파일에 플래그를 넣으면 남의 브랜치까지 따라 바뀐다.
 */
const USE_MOCK = false;

const BASE = '/contract/apis/unrestricted/chat';
const ADMIN_BASE = '/contract/apis/admin/chat';

// ── 고객 응답 타입 ───────────────────────────────────

/**
 * 응답의 성격. **넷 다 HTTP 200 이다.**
 *
 * `FALLBACK` 을 오류로 처리하지 말 것 — LLM 장애는 정상 동작 범위다(설계 §2).
 *
 * - `NORMAL`    추천 카드가 붙는다
 * - `FOLLOW_UP` 되묻기. **카드가 없다.** 세션당 최대 5회(상한은 서버 설정
 *               `chat.follow-up-max`)라 **연속으로 여러 번 올 수 있다** — 2026-08-28 에
 *               1회에서 늘었다. 화면은 횟수를 세지 않는다. 서버가 주는 대로 그린다
 * - `FALLBACK`  매칭 실패·LLM 장애. 카드가 붙는다 — 기본 3종일 수도, **되묻기를 다 쓴 뒤
 *               모델이 고른 근접 상품**일 수도 있다. 갈림은 `node` 로만 안다(아래)
 * - `REFUSAL`   금지발화 차단. 카드가 없다
 */
export type ChatMode = 'NORMAL' | 'FOLLOW_UP' | 'FALLBACK' | 'REFUSAL';

/**
 * 챗봇 카드가 그리는 상품.
 *
 * **카탈로그 응답 타입을 그대로 쓴다** (설계 §7 — 챗봇 전용 상품 DTO 를 만들지 않는다).
 * 같은 타입을 보므로 가격 페이지와 금액이 갈릴 수 없다. 확장은 `ctaLabel` 하나뿐이다.
 */
export interface ChatProduct extends CatalogProduct {
  /**
   * 버튼에 그대로 찍는 문장. **서버가 문안 DB 에서 채운다.**
   *
   * 프론트가 `cta` 로 라벨을 고르지 않는다 — 고르는 순간 그 문장이 코드에 박히고
   * 어드민에서 못 고친다.
   */
  ctaLabel: string;
}

/** 폴백 질문 트리의 선택지. `label` 도 서버가 채운 완성 문장이다. */
export interface FallbackOption {
  optionId: string;
  label: string;
}

/**
 * 폴백 질문 트리 노드. LLM 이 죽었을 때 대화를 끝까지 끌고 가는 경로다(설계 §2).
 *
 * 선택지 형태인 것은 이 경로뿐이다. `FOLLOW_UP` 되묻기는 **서술을 청하는 문장**이라
 * 선택지를 내지 않는다(설계 §5.2).
 */
export interface FallbackNode {
  nodeId: string;
  options: FallbackOption[];
}

/**
 * 폴백 사유. **관측용이다 — 화면이 이 값으로 분기하지 않는다.**
 *
 * 고객에게 원인을 구분해 보여줄 이유가 없어서 6종이 같은 문안으로 묶여 있고, 구분은
 * `chat_llm_call.outcome` 에 남는다(설계 §3). 여기 적어 두는 것은 **무엇이 오는지 읽는
 * 사람이 알게** 하려는 것뿐이라, 서버가 값을 늘려도 화면은 안 깨진다.
 */
export type FallbackReason = 'LOW_CONFIDENCE' | 'UNKNOWN_SERVICE' | 'SCHEMA_INVALID' | 'TIMEOUT' | 'CIRCUIT_OPEN' | 'RATE_LIMITED' | 'OUTPUT_FILTERED';

/**
 * 세션 시작·자유 발화·선택지 응답이 **모두 같은 모양**을 낸다.
 *
 * `disclaimer` 가 매번 오는 것은 설계 그대로다 — 화면은 이 값을 대화창 상단에 **상시**
 * 표시한다. 응답마다 말풍선으로 뿌리는 것이 아니다.
 *
 * **옵셔널 필드가 없다.** 서버가 `@JsonInclude(NON_NULL)` 을 붙이지 않기로 해서
 * (백엔드 회신 2026-08-20) 값이 없어도 `null` 로 내려온다. `undefined` 와 `null` 을
 * 가르는 분기를 프론트에 만들지 않는다.
 */
export interface ChatTurn {
  sessionUuid: string;
  mode: ChatMode;
  /** 대화창 상단 고지. 모든 응답에 포함된다 */
  disclaimer: string;
  /** 이번 턴에 뜰 문장. 세션 시작이면 인사말이 여기 온다 */
  message: string;
  /** `FOLLOW_UP` · `REFUSAL` 이면 빈 배열이다 */
  products: ChatProduct[];
  /**
   * `null` 이면 선택지를 그리지 않는다. **화면이 폴백의 두 모양을 가르는 유일한 축이다.**
   *
   * 같은 `FALLBACK`(심지어 같은 `fallbackReason: LOW_CONFIDENCE`)이 두 모양으로 온다 —
   * 되묻기가 남았으면 기본 3종 + 트리(`node` 있음), **되묻기를 다 쓴 뒤면 `node: null`**
   * (2026-08-28, 다섯 번 물은 고객을 처음부터 다시 고르는 트리로 보내지 않는다는 제품
   * 결정). 그때 카드는 대개 모델이 고른 근접 상품이고, 고른 것이 전부 비노출이면 기본
   * 3종이다 — 어느 쪽이든 `node` 는 `null` 이다. 그래서 `fallbackReason` 으로도 카드
   * 내용으로도 분기하면 안 된다. 축은 `node` 하나다.
   */
  node: FallbackNode | null;
  /**
   * 관측용. 화면에 그리지 않는다.
   *
   * **`refusalReason` 과 달리 이 값은 남는다.** 이쪽은 LLM 이 왜 못 골랐나(타임아웃·신뢰도
   * 부족·매칭 실패)라서 우회 힌트가 되지 않는다.
   */
  fallbackReason: FallbackReason | null;
  /** 이 세션에 되묻기가 남았는가. 상한은 서버 설정(`chat.follow-up-max`, 기본 5)이다(설계 §3) */
  followUpAvailable: boolean;

  // ⚠️ `refusalReason` 을 여기 다시 넣지 마라 (2026-08-22, 백엔드 PR #60 게이트 리뷰 라운드 2).
  //
  // 원래는 차단 사유별로 다른 문안이 나갔다 — 「다른 분의 개인정보는 확인해 드릴 수
  // 없습니다」 같은 식이다. **사유를 알려주는 것은 우회 힌트를 주는 것이다.** 무엇에 걸렸는지
  // 알면 표현만 바꿔 다시 시도한다. 그래서 고객에게 나가는 거절 문구를 하나로 통일했다.
  //
  // 그런데 문장만 통일하고 `refusalReason` 필드는 남겨 두어서, `ILLEGAL_ASSISTANCE` ·
  // `THIRD_PARTY_PII` 가 JSON 으로 그대로 샜다. **힌트를 없애자는 결정이 자리만 옮겨
  // 무력화된 상태였다.** 그래서 고객 응답에서 필드째 뺐다.
  //
  // 사유는 사라진 것이 아니라 **고객이 못 보는 곳에만 남는다** — 서버 기록(`chat_message.
  // block_reason` · `chat_llm_call`)과 어드민 dry-run 이다. 과차단 관찰은 그쪽에서 한다.
  // 「관측용이니 받아만 두자」는 것이 정확히 이 필드가 처음 들어온 이유다.
}

// ── 어드민 타입 ──────────────────────────────────────

export type ChatCopyCategory = 'GREETING' | 'DISCLAIMER' | 'FOLLOW_UP' | 'REFUSAL' | 'FALLBACK' | 'CTA' | 'TEMPLATE';

/**
 * 관리자 문안 한 줄.
 *
 * **고칠 수 있는 칸은 `textKo` 하나뿐이다.** `copyKey` 가 바뀌면 코드가 문안을 못 찾고,
 * `label`·`hint`·`category` 는 시드가 소유한다(설계 §5.1).
 */
export interface ChatCopy {
  id: number;
  /** 코드가 참조하는 상수. 불변 */
  copyKey: string;
  /** 어드민 화면 항목 이름 */
  label: string;
  /** 어디에 쓰이는 문안인지 */
  hint: string;
  /** 관리자가 고치는 유일한 칸 */
  textKo: string;
  category: ChatCopyCategory;
  sortOrder: number;
  /** ISO-8601. 예: `2026-08-20T14:22:10` */
  updatedAt: string;
  /** 서버가 인증 주체에서 꺼낸다. 요청에 싣지 않는다 */
  updatedBy: string | null;
}

/**
 * 문안이 아직 없는 자리. **서버가 대조해 준다 — 프론트가 카탈로그와 맞추지 않는다.**
 *
 * 「어느 카테고리에 문안이 없나」를 프론트가 계산하면 그 규칙이 두 군데가 된다.
 * `POST /copy` 가 받는 두 칸(`derivedFrom`·`sourceKey`)이 여기서 그대로 나오므로,
 * 화면은 이 목록을 그리고 `textKo` 만 받아 그대로 되돌려 보내면 된다.
 */
export interface MissingChatCopy {
  derivedFrom: 'CATEGORY' | 'FOLLOW_UP';
  /** 카탈로그 카테고리 코드 또는 되묻기 질문의 `followUpKey` */
  sourceKey: string;
  /** 서버가 만들 키. **화면은 보여주기만 한다 — 입력칸이 아니다** */
  suggestedCopyKey: string;
  label: string;
  hint: string;
}

/** `GET /copy` 응답. 있는 문안과 **비어 있는 자리**가 같이 온다 */
export interface ChatCopyList {
  items: ChatCopy[];
  missing: MissingChatCopy[];
}

/**
 * 문안 신규 생성. **키를 사람이 입력하지 않는다 — 서버가 파생시킨다.**
 *
 *   `CATEGORY`  → `TEMPLATE_RECOMMEND_{sourceKey}`  (카탈로그 카테고리 코드)
 *   `FOLLOW_UP` → `FOLLOW_UP_{sourceKey}`           (되묻기 질문의 follow_up_key)
 *
 * 자유 입력 폼을 만들면 「관리자는 `text_ko` 만 고친다」가 무너진다. 이미 있으면 409 다.
 */
export interface CreateChatCopyRequest {
  derivedFrom: 'CATEGORY' | 'FOLLOW_UP';
  sourceKey: string;
  textKo: string;
}

/**
 * 되묻기 질문. **`criterion` 이 이 테이블의 핵심이다.**
 *
 * 「이걸 모르면 상품을 못 고른다」를 변호사가 자기 말로 쓰고, 그 문장이 **그대로 LLM
 * 프롬프트에 들어간다.** 개발자가 손댈 것이 없다(설계 §5.2).
 */
export interface ChatFollowUp {
  id: number;
  /** LLM 이 고르는 값. 불변 */
  followUpKey: string;
  label: string;
  /** 무엇을 판단하려는 질문인가 — 프롬프트로 들어간다 */
  criterion: string;
  /** 실제 나갈 문안 (`ChatCopy.copyKey`) */
  copyKey: string;
  /** 특정 카테고리 전용이면 그 코드. null 이면 공통 */
  categoryCode: string | null;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
  updatedBy: string | null;
}

/**
 * 질문 생성. **문안까지 한 번에 만든다** — 서버가 `FOLLOW_UP_{followUpKey}` 로 문안 행을
 * 함께 만든다. 질문만 생기고 문안이 없으면 LLM 이 고를 수는 있는데 낼 문장이 없다.
 */
export interface CreateFollowUpRequest {
  /** UPPER_SNAKE. 예: `ASK_CONTRACT_TYPE` */
  followUpKey: string;
  label: string;
  criterion: string;
  categoryCode: string | null;
  enabled: boolean;
  /** 이 질문이 실제로 낼 문장 */
  textKo: string;
}

/** 수정. `followUpKey` 와 `copyKey` 는 불변이라 여기 없다 */
export interface UpdateFollowUpRequest {
  label: string;
  criterion: string;
  categoryCode: string | null;
  enabled: boolean;
}

export type GuardStage = 'INPUT' | 'OUTPUT';

/** 차단 규칙. 시드 9종은 설계 §11.1 이 근거 조문과 함께 들고 있다 */
export interface ChatGuardRule {
  id: number;
  stage: GuardStage;
  pattern: string;
  /** `REFUSAL_*` 문안 key 로 연결된다 */
  reasonKey: string;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
  updatedBy: string | null;
}

export interface UpdateGuardRuleRequest {
  pattern: string;
  reasonKey: string;
  enabled: boolean;
}

/**
 * 100건 회귀 한 건의 판정.
 *
 * `OVER_BLOCK` 이 이 화면의 존재 이유다 — "해고"·"유출"·"소송"·"미지급"은 전부 정상
 * 상담어라, 규칙을 넓게 잡으면 진짜 고객을 막는다(설계 §4.1).
 *
 * - `PASS`         기대와 실제가 같다
 * - `OVER_BLOCK`   통과해야 할 문장을 막았다 ★
 * - `UNDER_BLOCK`  막아야 할 문장이 나갔다 ★
 * - `MISMATCH`     그 밖의 불일치 (예: `FALLBACK` 기대인데 `REFUSAL`)
 */
export type DryRunVerdict = 'PASS' | 'OVER_BLOCK' | 'UNDER_BLOCK' | 'MISMATCH';

export type DryRunOutcome = 'PASS' | 'REFUSAL' | 'FALLBACK';

export interface DryRunCase {
  id: string;
  category: string;
  stage: GuardStage;
  input: string;
  expected: DryRunOutcome;
  actual: DryRunOutcome;
  verdict: DryRunVerdict;
  /** 어느 규칙이 걸었나. null 이면 규칙이 안 걸렸다 — 과차단을 **고치려면** 이 값이 있어야 한다 */
  matchedRuleId: number | null;
  matchedPattern: string | null;
}

/**
 * 규칙 한 줄이 100건을 얼마나 잡았나.
 *
 * **규칙이 얼마나 넓은지는 건별 목록으로는 안 보인다.** 방금 고친 그 규칙이 몇 건을 잡았고
 * 그중 몇 건이 과차단이었나 — 그게 「너무 넓게 잡았다」의 실제 신호다.
 */
export interface DryRunRuleHit {
  ruleId: number;
  pattern: string;
  stage: GuardStage;
  hitCount: number;
  overBlockCount: number;
}

export interface DryRunResult {
  ranAt: string;
  totalCases: number;
  /**
   * 무엇으로 돌렸나. **`DRAFT` 면 저장 전 규칙이다.**
   *
   * 화면이 「저장 전 규칙으로 돌린 결과입니다」를 그릴 근거가 이 값뿐이다 — 없으면
   * 저장 전 결과를 저장 후 결과로 착각한다.
   */
  ruleSetSource: 'DRAFT' | 'SAVED';
  summary: {
    pass: number;
    overBlock: number;
    underBlock: number;
    mismatch: number;
  };
  cases: DryRunCase[];
  ruleHits: DryRunRuleHit[];
}

/** dry-run 이 받는 규칙 한 줄. `id` 가 null 이면 아직 저장 안 한 새 규칙이다. */
export type DryRunRule = Pick<ChatGuardRule, 'stage' | 'pattern' | 'reasonKey' | 'enabled' | 'sortOrder'> & { id: number | null };

/**
 * dry-run 요청. **저장 전 규칙을 실어 보낸다.**
 *
 * 저장한 뒤에만 돌 수 있다면 과차단은 이미 운영에 나간 뒤다 — 설계 §11.2 의 「규칙을 고치면
 * 그 자리에서 과차단이 생겼는지 즉시 본다」가 성립하지 않는다. 생략하면 저장된 규칙으로 돈다.
 *
 * 평가셋 100건은 **서버 소유**다. 프론트가 케이스를 올리지 않는다.
 */
export interface DryRunRequest {
  rules?: DryRunRule[];
}

// ── 오류·봉투 ────────────────────────────────────────

/**
 * 상태 코드가 붙은 오류. 화면이 403(권한 없음)과 409(이미 있음)를 다르게 그려야 해서 필요하다.
 *
 * 정의는 `chat.error.ts` 에 있다 — 목도 같은 오류를 던지는데, 목이 이 파일을 import 하면
 * 순환이 된다. 화면은 계속 `@/lib/api/chat` 에서 가져다 쓴다.
 */
export { ChatApiError };

interface Envelope<T> {
  status?: number;
  code?: string;
  message?: string;
  result?: T;
}

const FALLBACK_MESSAGE: Record<number, string> = {
  400: '입력값을 다시 확인해 주세요.',
  403: '이 항목을 변경할 권한이 없습니다.',
  404: '해당 항목을 찾을 수 없습니다.',
  409: '이미 있는 문안입니다.',
};

function messageFor(status: number, serverMessage?: string): string {
  return serverMessage?.trim() || FALLBACK_MESSAGE[status] || '요청을 처리하지 못했습니다.';
}

/**
 * 봉투를 연다. **본문 `status` 가 진짜 상태다** — HTTP 상태가 아니다.
 *
 * 게이트웨이는 에러도 HTTP 200 으로 내려주고 진짜 상태를 본문에 담는다(실측 —
 * `catalog.ts` 주석). `res.ok` 만 보면 403 이 성공으로 통과해서, 권한이 없는데도
 * 화면이 "저장됨"으로 보인다.
 */
function unwrap<T>(response: AxiosResponse<Envelope<T>>): T {
  const body = response.data;
  const status = body?.status ?? response.status;

  if (status !== 200 || body?.result === undefined) {
    throw new ChatApiError(status, messageFor(status, body?.message));
  }
  return body.result;
}

function toChatError(error: unknown): ChatApiError {
  if (error instanceof ChatApiError) return error;

  const response = (error as { response?: { status?: number; data?: { status?: number } } })?.response;
  const status = response?.data?.status ?? response?.status ?? 0;
  const message = status === 0 ? '서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.' : apiErrorMessage(error, messageFor(status));

  return new ChatApiError(status, message);
}

/** 모든 호출을 같은 오류 타입으로 통일한다. 화면은 `ChatApiError.status` 하나만 보면 된다. */
async function call<T>(request: () => Promise<AxiosResponse<Envelope<T>>>): Promise<T> {
  try {
    return unwrap(await request());
  } catch (error) {
    throw toChatError(error);
  }
}

// ── 고객 API ─────────────────────────────────────────

/** 세션 시작. 응답 `message` 에 인사말이, `disclaimer` 에 고지가 온다. */
export async function startChatSession(): Promise<ChatTurn> {
  if (USE_MOCK) return mock.startChatSession();
  return call<ChatTurn>(() => axiosInstance.post(`${BASE}/sessions`, {}));
}

/** 자유 발화. 기본 경로다. */
export async function sendChatMessage(sessionUuid: string, text: string): Promise<ChatTurn> {
  if (USE_MOCK) return mock.sendChatMessage(sessionUuid, text);
  return call<ChatTurn>(() => axiosInstance.post(`${BASE}/sessions/${encodeURIComponent(sessionUuid)}/message`, { text }));
}

/** 선택지 응답. LLM 이 죽었을 때의 폴백 트리 경로다. */
export async function sendChatAnswer(sessionUuid: string, nodeId: string, optionId: string): Promise<ChatTurn> {
  if (USE_MOCK) return mock.sendChatAnswer(sessionUuid, nodeId, optionId);
  return call<ChatTurn>(() => axiosInstance.post(`${BASE}/sessions/${encodeURIComponent(sessionUuid)}/answer`, { nodeId, optionId }));
}

/**
 * CTA 클릭 적재. **프론트가 보내는 이벤트는 이것 하나뿐이다.**
 *
 * `CARD_SHOWN` 을 보내지 않는다 — 서버가 조립 시점에 이미 안다. 클라 보고에 의존하면
 * 이탈·애드블록으로 누락돼 **깔때기 분모가 작아지고 전환율이 실제보다 높게 나온다**(설계 §5.4).
 *
 * 실패해도 던지지 않는다. 이 호출은 계측이고, 뒤에 이어지는 것은 고객의 결제·상담 진입이다.
 * 계측 실패로 그 길을 막으면 재려는 대상 자체가 줄어든다.
 */
export async function reportCtaClicked(sessionUuid: string, productCode: string): Promise<void> {
  try {
    if (USE_MOCK) {
      await mock.reportCtaClicked(sessionUuid, productCode);
      return;
    }
    await axiosInstance.post(`${BASE}/sessions/${encodeURIComponent(sessionUuid)}/events`, { type: 'CTA_CLICKED', productCode });
  } catch {
    // 계측 실패는 고객 흐름을 막지 않는다. 누락은 서버가 적재하는 CARD_SHOWN 과의 대비로 드러난다.
  }
}

// ── 어드민 API — 문안 ────────────────────────────────

/** 있는 문안과 **비어 있는 자리**가 같이 온다. 대조는 서버가 한다. */
export async function fetchChatCopies(): Promise<ChatCopyList> {
  if (USE_MOCK) return mock.fetchChatCopies();
  return call<ChatCopyList>(() => axiosInstance.get(`${ADMIN_BASE}/copy`));
}

/** 고칠 수 있는 것은 `textKo` 뿐이다. key 는 경로로만 간다. */
export async function updateChatCopy(copyKey: string, textKo: string): Promise<ChatCopy> {
  if (USE_MOCK) return mock.updateChatCopy(copyKey, textKo);
  return call<ChatCopy>(() => axiosInstance.put(`${ADMIN_BASE}/copy/${encodeURIComponent(copyKey)}`, { textKo }));
}

/** 키는 서버가 파생시킨다. `copyKey` 를 보내지 않는다. */
export async function createChatCopy(request: CreateChatCopyRequest): Promise<ChatCopy> {
  if (USE_MOCK) return mock.createChatCopy(request);
  return call<ChatCopy>(() => axiosInstance.post(`${ADMIN_BASE}/copy`, request));
}

// ── 어드민 API — 되묻기 질문 ─────────────────────────

export async function fetchFollowUps(): Promise<ChatFollowUp[]> {
  if (USE_MOCK) return mock.fetchFollowUps();
  return call<ChatFollowUp[]>(() => axiosInstance.get(`${ADMIN_BASE}/follow-ups`));
}

export async function createFollowUp(request: CreateFollowUpRequest): Promise<ChatFollowUp> {
  if (USE_MOCK) return mock.createFollowUp(request);
  return call<ChatFollowUp>(() => axiosInstance.post(`${ADMIN_BASE}/follow-ups`, request));
}

export async function updateFollowUp(id: number, request: UpdateFollowUpRequest): Promise<ChatFollowUp> {
  if (USE_MOCK) return mock.updateFollowUp(id, request);
  return call<ChatFollowUp>(() => axiosInstance.put(`${ADMIN_BASE}/follow-ups/${id}`, request));
}

// ── 어드민 API — 차단 규칙 ───────────────────────────

export async function fetchGuardRules(): Promise<ChatGuardRule[]> {
  if (USE_MOCK) return mock.fetchGuardRules();
  return call<ChatGuardRule[]>(() => axiosInstance.get(`${ADMIN_BASE}/guard-rules`));
}

export async function updateGuardRule(id: number, request: UpdateGuardRuleRequest): Promise<ChatGuardRule> {
  if (USE_MOCK) return mock.updateGuardRule(id, request);
  return call<ChatGuardRule>(() => axiosInstance.put(`${ADMIN_BASE}/guard-rules/${id}`, request));
}

/** 「이 규칙으로 100건 돌려보기」. 읽기 동작이라 ADMIN·LAWYER 둘 다 부를 수 있다. */
export async function dryRunGuardRules(request: DryRunRequest): Promise<DryRunResult> {
  if (USE_MOCK) return mock.dryRunGuardRules(request);
  return call<DryRunResult>(() => axiosInstance.post(`${ADMIN_BASE}/guard-rules/dry-run`, request));
}
